import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { formatCoefficient } from '../expression.utils';
import { BaseFunctionCreator } from '../function.model';
import { QUADRATIC_META, type QuadraticParams } from './quadratic.model';

type QuadraticParamsIndexed = QuadraticParams & { [key: string]: number };

export class QuadraticCreator extends BaseFunctionCreator<QuadraticParamsIndexed> {
  readonly functionType = 'quadratic';
  readonly defaultParams: QuadraticParamsIndexed = { ...QUADRATIC_META.defaultParams };
  readonly availableFeatures = QUADRATIC_META.availableFeatures;
  readonly handleConfigs = QUADRATIC_META.handleConfigs;
  override readonly computedValueKeys = ['vertexX', 'vertexY', 'discriminant', 'yIntercept'] as const;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as QuadraticParamsIndexed;

    const colorName = (display?.curve?.color ?? 'sky') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: QUADRATIC_META.defaultRanges.a },
      b: { value: initialParams.b, range: QUADRATIC_META.defaultRanges.b },
      c: { value: initialParams.c, range: QUADRATIC_META.defaultRanges.c },
    });

    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const b = paramStore.getValue('b');
      const c = paramStore.getValue('c');
      return a * x * x + b * x + c;
    };

    const getExpression = (): string => {
      const a = paramStore.getDisplayValue('a');
      const b = paramStore.getDisplayValue('b');
      const c = paramStore.getDisplayValue('c');
      return buildStandardFormExpression(a, b, c);
    };

    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
      getExpression,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    const getDerivative = (order = 1): ((x: number) => number) => {
      if (order === 1) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          return 2 * a * x + b;
        };
      }
      if (order === 2) {
        return () => 2 * paramStore.getValue('a');
      }
      return () => 0;
    };

    return {
      id,
      type: 'quadratic',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as QuadraticParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      setDisplaySteps: (steps) => {
        for (const [name, step] of Object.entries(steps)) {
          paramStore.setDisplayStep(name, step);
        }
      },
      getDerivative,
      getExpression,
      getComputedValues: () => ({
        vertexX: () => -paramStore.getValue('b') / (2 * paramStore.getValue('a')),
        vertexY: () => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          return c - (b * b) / (4 * a);
        },
        discriminant: () => {
          const b = paramStore.getValue('b');
          return b * b - 4 * paramStore.getValue('a') * paramStore.getValue('c');
        },
        yIntercept: () => paramStore.getValue('c'),
      }),
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}

/**
 * Build a LaTeX string for a quadratic in standard form: ax² + bx + c
 * Simplifies common cases (a=1, a=-1, b=0, c=0).
 */
function buildStandardFormExpression(a: number, b: number, c: number): string {
  const fmt = formatCoefficient;
  const parts: string[] = [];

  const isAOne = Math.abs(a - 1) < 1e-9;
  const isANegOne = Math.abs(a + 1) < 1e-9;
  const isBZero = Math.abs(b) < 1e-9;
  const isCZero = Math.abs(c) < 1e-9;

  // ax² term
  if (isAOne) {
    parts.push('x^2');
  } else if (isANegOne) {
    parts.push('-x^2');
  } else {
    parts.push(`${fmt(a)}x^2`);
  }

  // bx term
  if (!isBZero) {
    if (Math.abs(b - 1) < 1e-9) {
      parts.push('+ x');
    } else if (Math.abs(b + 1) < 1e-9) {
      parts.push('- x');
    } else if (b > 0) {
      parts.push(`+ ${fmt(b)}x`);
    } else {
      parts.push(`- ${fmt(-b)}x`);
    }
  }

  // c term
  if (!isCZero) {
    if (c > 0) {
      parts.push(`+ ${fmt(c)}`);
    } else {
      parts.push(`- ${fmt(-c)}`);
    }
  }

  return `$${parts.join(' ')}$`;
}
